import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

/**
 * Cliente de Firebase del panel.
 *
 * Los valores de `firebaseConfig` son publicos por diseno: viajan dentro del
 * bundle y cualquiera puede leerlos. Lo que protege los datos no es
 * ocultarlos, sino las Security Rules y App Check. Por eso no tiene sentido
 * tratarlos como secretos.
 */

const env = import.meta.env;

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

const missing = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missing.length > 0) {
  // Fallar aqui y con un mensaje claro ahorra un rato de depuracion: sin
  // esto, el primer sintoma es un error opaco del SDK al iniciar sesion.
  throw new Error(
    `Falta configuracion de Firebase: ${missing.join(', ')}. Copia .env.example a .env y rellenalo.`,
  );
}

export const app: FirebaseApp = initializeApp(firebaseConfig);

export const USE_EMULATORS = env.VITE_USE_EMULATORS === 'true';

/**
 * App Check demuestra que la peticion viene de nuestra aplicacion y no de un
 * script. Sin el, cualquiera con el ID de proyecto puede llamar a las
 * funciones y leer lo que las reglas permitan.
 *
 * Se usa reCAPTCHA Enterprise y no el v3 clasico: Google migro las claves
 * clasicas a Enterprise durante 2026 y la consola de App Check marca el v3
 * como obsoleto. Enterprise incluye 10.000 evaluaciones al mes sin coste.
 *
 * El token de depuracion tiene que asignarse ANTES de inicializar App Check,
 * porque el SDK lo lee del objeto global durante la inicializacion. Si se
 * asigna despues no sirve de nada.
 */
if (env.DEV && env.VITE_APPCHECK_DEBUG_TOKEN) {
  // La clave de reCAPTCHA de produccion no admite localhost entre sus
  // dominios, asi que en desarrollo la unica via es el token de depuracion,
  // que se registra a mano en la consola de Firebase.
  // @ts-expect-error: propiedad que el SDK lee del objeto global en desarrollo
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = env.VITE_APPCHECK_DEBUG_TOKEN;
}

// Contra los emuladores no hace falta App Check: no se emiten tokens y las
// funciones lo eximen expresamente.
if (!USE_EMULATORS && env.VITE_APPCHECK_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(env.VITE_APPCHECK_SITE_KEY),
    // Sin esto el token caduca a la hora y las llamadas empiezan a fallar en
    // mitad de una sesion.
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-east1');
export const storage = getStorage(app);

if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

/** Base del enlace corto, para componer la URL que se codifica en el QR. */
export const SHORT_LINK_BASE: string =
  env.VITE_SHORT_LINK_BASE ?? 'http://127.0.0.1:5000';

/**
 * Dominio publico del almacenamiento de archivos.
 *
 * En la base de datos se guarda la RUTA del archivo, no su direccion; esto es
 * lo que la compone. Asi, el dia que el almacenamiento cambie de dominio basta
 * con cambiar esta variable: los codigos ya creados siguen llevando a su
 * archivo sin tocar ni uno.
 */
export const R2_PUBLIC_BASE: string = env.VITE_R2_PUBLIC_BASE ?? '';
